import { useMutation } from 'react-query';

import * as api from '../../../services/api';
import { ApiRequestError, attachApiRequestErrorContext } from '../../../services/api';
import { UpdateRegistrationDTO } from '../../../services/types';
import { UserInput } from '../../../types/registration/userInput';
import { queryClient, queryKeys } from '../../../utils/react-query';

interface UpdateRegistrationInput {
  registrationId?: string;
  userInput: UserInput;
}

export const useUpdateRegistration = (registrationId?: string) => {
  const { mutate, mutateAsync, isLoading, error, reset } = useMutation<
    UpdateRegistrationDTO,
    ApiRequestError,
    UpdateRegistrationInput
  >(
    (input: UpdateRegistrationInput) =>
      api.updateRegistration(input.registrationId, input.userInput).catch((requestError: unknown) => {
        if (requestError instanceof ApiRequestError) {
          throw attachApiRequestErrorContext(requestError, {
            registrationId: input.registrationId,
            route: typeof window !== 'undefined' ? window.location.pathname : undefined,
            step: 'updateRegistration'
          });
        }
        throw requestError;
      }),
    {
      onSuccess: () => queryClient.invalidateQueries([queryKeys.userInput, registrationId])
    }
  );

  const updateRegistration = (
    input: UpdateRegistrationInput,
    options?: Parameters<typeof mutate>[1]
  ) => {
    reset();
    mutate(input, options);
  };

  const updateRegistrationAsync = (input: UpdateRegistrationInput) => {
    reset();
    return mutateAsync(input);
  };

  return {
    updateRegistration,
    updateRegistrationAsync,
    updateIsLoading: isLoading,
    updateError: error ?? null,
    resetUpdateError: reset
  };
};
